import { Controller } from "@hotwired/stimulus";

// 要素に追加するプロパティのための型定義
interface ElementWithAnimation extends HTMLElement {
  fadeAnimation?: number;
}

// Connects to data-controller="footer-button"
export default class extends Controller {
  static targets = ["footerBtn"];

  // 型定義
  declare readonly footerBtnTarget: ElementWithAnimation;
  declare readonly hasFooterBtnTarget: boolean;

  private scrollHandler: () => void;

  connect(): void {
    this.scrollHandler = this.handleScroll.bind(this);
    window.addEventListener("scroll", this.scrollHandler);
    this.handleScroll();
  }

  disconnect(): void {
    window.removeEventListener("scroll", this.scrollHandler);
  }

  handleScroll(): void {
    const pageHeight = document.documentElement.scrollHeight;
    const scrollTop = window.scrollY;
    const windowHeight = window.innerHeight;
    const relativePosition = pageHeight - windowHeight - scrollTop;
    const BASE_VALUE = 200;

    // 上&下から基準値までスクロールすると表示切替え
    if (this.hasFooterBtnTarget) {
      if (relativePosition < BASE_VALUE) {
        this.fadeOut(this.footerBtnTarget);
      } else {
        this.fadeIn(this.footerBtnTarget);
      }
    }
  }

  // jQueryのfadeIn/fadeOutのよりなめらかな実装
  fadeIn(element: ElementWithAnimation): void {
    if (element.style.opacity !== "1") {
      // アニメーション中なら中断
      if (element.fadeAnimation) {
        cancelAnimationFrame(element.fadeAnimation);
        element.fadeAnimation = undefined;
      }

      element.style.display = "block";
      const startOpacity = parseFloat(element.style.opacity) || 0;
      const startTime = performance.now();
      const duration = 300; // ミリ秒単位でアニメーション時間を設定（jQueryのデフォルトは400ms）

      const animate = (currentTime: number): void => {
        const elapsedTime = currentTime - startTime;
        let progress = elapsedTime / duration;

        // イージング関数（jQueryと同様にeaseOutQuadを使用）
        progress = progress > 1 ? 1 : 1 - Math.pow(1 - progress, 2);

        const currentOpacity = startOpacity + (1 - startOpacity) * progress;
        element.style.opacity = currentOpacity.toString();

        if (progress < 1) {
          element.fadeAnimation = requestAnimationFrame(animate);
        } else {
          element.style.opacity = "1";
          element.fadeAnimation = undefined;
        }
      };

      element.fadeAnimation = requestAnimationFrame(animate);
    }
  }

  fadeOut(element: ElementWithAnimation): void {
    if (element.style.display !== "none") {
      // アニメーション中なら中断
      if (element.fadeAnimation) {
        cancelAnimationFrame(element.fadeAnimation);
        element.fadeAnimation = undefined;
      }

      const startOpacity = parseFloat(element.style.opacity) || 1;
      const startTime = performance.now();
      const duration = 300; // ミリ秒単位でアニメーション時間を設定

      const animate = (currentTime: number): void => {
        const elapsedTime = currentTime - startTime;
        let progress = elapsedTime / duration;

        // イージング関数（jQueryと同様にeaseOutQuadを使用）
        progress = progress > 1 ? 1 : 1 - Math.pow(1 - progress, 2);

        const currentOpacity = startOpacity * (1 - progress);
        element.style.opacity = currentOpacity.toString();

        if (progress < 1) {
          element.fadeAnimation = requestAnimationFrame(animate);
        } else {
          element.style.opacity = "0";
          element.style.display = "none";
          element.fadeAnimation = undefined;
        }
      };

      element.fadeAnimation = requestAnimationFrame(animate);
    }
  }
}
